<script setup lang="ts">
import { computed, ref } from 'vue';
import type { NyxTask } from '@atum-remote/nyx-link-protocol';
import { formatRelativeTime, formatTimestamp } from '../lib/format.js';
import {
  clientShortLabel,
  groupTasksByProject,
  stateLabel,
  stateTone,
  taskTimeIso,
} from '../lib/nyx-presentation.js';
import AppIcon from './AppIcon.vue';

/**
 * The conversation list, grouped by repository like the thread lists in Codex
 * and Claude Code. Each project folds; a project that needs a person surfaces
 * first and carries a badge.
 */
const props = defineProps<{
  tasks: readonly NyxTask[];
  selectedId: string | null;
  truncated: boolean;
  projectNames: Readonly<Record<string, string>>;
  busy: boolean;
  canCreate: boolean;
}>();
const emit = defineEmits<{ select: [task: NyxTask]; create: [projectKey: string] }>();
const collapsed = ref(new Set<string>());
const groups = computed(() => groupTasksByProject(props.tasks, props.projectNames));
function toggle(projectKey: string) {
  const next = new Set(collapsed.value);
  if (next.has(projectKey)) next.delete(projectKey);
  else next.add(projectKey);
  collapsed.value = next;
}
</script>

<template>
  <div class="nyx-list" role="group" aria-label="Hosted tasks">
    <p v-if="!tasks.length" class="nyx-list-empty">
      No hosted tasks yet. Start one and it appears here under its repository, shared with Nyx
      Link's Chat view.
    </p>
    <section
      v-for="group in groups"
      :key="group.projectKey"
      class="nyx-project"
      :data-open="collapsed.has(group.projectKey) ? 'false' : 'true'"
    >
      <div class="nyx-project-head">
        <button
          type="button"
          class="nyx-project-toggle"
          :aria-expanded="collapsed.has(group.projectKey) ? 'false' : 'true'"
          :aria-controls="`nyx-project-${group.projectKey}`"
          @click="toggle(group.projectKey)"
        >
          <AppIcon name="chevronDown" class="nyx-project-chevron" />
          <AppIcon name="folder" class="nyx-project-icon" />
          <span class="nyx-project-name" :title="group.projectKey">{{ group.name }}</span>
          <span
            v-if="group.waiting"
            class="nyx-badge"
            :title="`${group.waiting} waiting for you`"
            >{{ group.waiting }}</span
          >
          <span
            v-else
            class="nyx-project-count"
            :title="`${group.tasks.length} tasks, ${group.active} active`"
            >{{ group.tasks.length }}</span
          >
        </button>
        <button
          v-if="canCreate"
          type="button"
          class="icon-button quiet small nyx-project-new"
          :aria-label="`New task in ${group.name}`"
          :title="`New task in ${group.name}`"
          :disabled="busy"
          @click="emit('create', group.projectKey)"
        >
          <AppIcon name="plus" />
        </button>
      </div>
      <div
        v-show="!collapsed.has(group.projectKey)"
        :id="`nyx-project-${group.projectKey}`"
        class="nyx-project-tasks"
      >
        <button
          v-for="task in group.tasks"
          :key="task.id"
          type="button"
          class="nyx-task"
          :aria-current="task.id === selectedId ? 'true' : undefined"
          :data-state="task.state"
          :disabled="busy"
          @click="emit('select', task)"
        >
          <span class="nyx-task-top">
            <span
              class="nyx-state"
              :data-tone="stateTone[task.state]"
              :data-pulse="task.state === 'running' ? 'true' : undefined"
              ><i class="pill-dot" aria-hidden="true" />{{ stateLabel[task.state] }}</span
            >
            <time
              class="nyx-task-time"
              :datetime="taskTimeIso(task.updatedAt) ?? undefined"
              :title="formatTimestamp(taskTimeIso(task.updatedAt))"
              >{{ formatRelativeTime(taskTimeIso(task.updatedAt)) }}</time
            >
          </span>
          <span class="nyx-task-meta">
            <span class="nyx-task-client">{{ clientShortLabel[task.client] }}</span>
            <span class="nyx-task-model mono">{{ task.model ?? 'default model' }}</span>
          </span>
        </button>
      </div>
    </section>
    <p v-if="truncated" class="hint">Showing the most recent tasks.</p>
  </div>
</template>
